import { FC } from 'react';
import { Menu, Overlay, Divider } from '@mantine/core';
import {
  IconUserPlus,
  IconUsers,
  IconTrash,
  IconHeartPlus,
  IconUserEdit,
  IconCopy,
  IconLink,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGraphData } from '../hooks/useGraphData';
import { usePedigreeContext } from '../context/usePedigreeContext';

// -----------------------------------------------------------------------------
interface NodeContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  selectedNode: number | undefined;
  onClose: () => void;
  nodeType?: 'person' | 'relationship';
}

// -----------------------------------------------------------------------------
export const NodeContextMenu: FC<NodeContextMenuProps> = ({
  isOpen,
  position,
  selectedNode = undefined,
  onClose,
  nodeType = 'person',
}) => {
  const { t } = useTranslation('pedigree');
  const {
    addChild,
    addParents,
    addPartner,
    addSibling,
    addTwin,
    hasParents,
    hasPartner,
    deleteNode,
  } = useGraphData();

  const { setSelectedNodeId, setRelationshipSelectionMode } =
    usePedigreeContext();

  // ---------------------------------------------------------------------------
  const handleStartRelationship = () => {
    if (selectedNode === undefined) {
      return;
    }

    setRelationshipSelectionMode({
      isActive: true,
      firstNodeId: selectedNode,
    });

    onClose();
  };

  // ---------------------------------------------------------------------------
  if (!isOpen) {
    return null;
  }

  // ---------------------------------------------------------------------------
  if (selectedNode === undefined) {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <>
      <Overlay onClick={onClose} backgroundOpacity={0.3} />
      <Menu
        opened={isOpen}
        onClose={onClose}
        position="bottom-start"
        shadow="md"
        withArrow={false}
      >
        <Menu.Dropdown
          style={{
            position: 'fixed',
            top: position.y,
            left: position.x,
            transform: 'translate(-50%, -50%)',
            zIndex: 2000,
          }}
        >
          <Menu.Item
            leftSection={<IconUserEdit size={16} />}
            onClick={() => {
              setSelectedNodeId(selectedNode);
              onClose();
            }}
          >
            {t('contextMenu.edit')}
          </Menu.Item>
          <Divider />
          {nodeType === 'person' && (
            <>
              {!hasParents(selectedNode) && (
                <Menu.Item
                  leftSection={<IconUserPlus size={16} />}
                  onClick={() => {
                    addParents(selectedNode);
                    onClose();
                  }}
                >
                  {t('contextMenu.addParents')}
                </Menu.Item>
              )}
              {hasPartner(selectedNode) && (
                <Menu.Item
                  leftSection={<IconUserPlus size={16} />}
                  onClick={() => {
                    addChild(selectedNode);
                    onClose();
                  }}
                >
                  {t('contextMenu.addChild')}
                </Menu.Item>
              )}{' '}
              <Menu.Item
                leftSection={<IconUsers size={16} />}
                onClick={() => {
                  addSibling(selectedNode);
                  onClose();
                }}
              >
                {t('contextMenu.addSibling')}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCopy size={16} />}
                onClick={() => {
                  addTwin(selectedNode);
                  onClose();
                }}
              >
                {t('contextMenu.addTwin' as any)}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconHeartPlus size={16} />}
                onClick={() => {
                  addPartner(selectedNode);
                  onClose();
                }}
              >
                {t('contextMenu.addNewPartner')}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconLink size={16} />}
                onClick={handleStartRelationship}
              >
                {t('contextMenu.addRelationship')}
              </Menu.Item>
              <Divider />
              <Menu.Item
                leftSection={<IconTrash size={16} />}
                color="red"
                onClick={() => {
                  deleteNode(selectedNode);
                  onClose();
                }}
              >
                {t('contextMenu.delete')}
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
};
